import { useState } from "react";
import { Flag } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import ReportContentSheet, { type ReportContentTarget } from "./ReportContentSheet";

/**
 * ReportContentButton — o controle VISIVEL de denuncia.
 *
 * Duas regras que nao sao estetica, sao o motivo da rejeicao de 18/08:
 *
 * 1. NADA DE GESTO ESCONDIDO. Long-press, swipe ou menu de tres pontos dentro
 *    de outro menu sao equivalentes a nao ter o recurso: quem revisa a
 *    submissao no Google abre o app por alguns minutos e precisa ACHAR o
 *    botao. Por isso e um icone de bandeira sempre renderizado ao lado do
 *    conteudo, com rotulo acessivel e alvo de toque de 32px.
 *
 * 2. O FLUXO INTEIRO ACONTECE AQUI. O toque abre um Drawer no mesmo processo —
 *    nao um `mailto:`, nao um link externo, nao um formulario web. A politica
 *    diz "without needing to exit the app" e as tres alternativas saem.
 *
 * A folha vive DENTRO do botao de proposito: cada call site vira uma linha e
 * nenhuma tela precisa carregar estado de denuncia. Sao 16 superficies em 22
 * pontos de render; se cada uma tivesse de gerenciar `open`/`target`, metade
 * nao ganharia o controle. (O numero vive em REPORT_SURFACES e e conferido por
 * src/test/contentReport.test.ts — este comentario nao e a fonte.)
 */

interface ReportContentButtonProps extends ReportContentTarget {
  /** "icon" = so a bandeira (bolhas, cards densos). "inline" = bandeira + texto. */
  variant?: "icon" | "inline";
  className?: string;
  /** Ver ReportContentSheet: chamado apos a linha estar gravada. */
  onReported?: () => void;
}

const ReportContentButton = ({
  variant = "icon",
  className,
  onReported,
  ...target
}: ReportContentButtonProps) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const label = t("report.button.label");

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // Muitas superficies tem o card inteiro clicavel (abrir, expandir,
          // reproduzir). Sem isto, denunciar tambem dispararia a acao do card.
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={label}
        title={label}
        className={
          "shrink-0 inline-flex items-center gap-1.5 rounded-lg transition-opacity active:opacity-60 " +
          (variant === "inline" ? "px-2 py-1.5 " : "w-8 h-8 justify-center ") +
          (className ?? "")
        }
        style={{ color: "hsl(210 16% 55%)" }}
      >
        <Flag size={variant === "inline" ? 13 : 14} />
        {variant === "inline" && (
          <span className="text-[12px] font-display">{label}</span>
        )}
      </button>

      <ReportContentSheet open={open} onOpenChange={setOpen} target={target} onReported={onReported} />
    </>
  );
};

export default ReportContentButton;
